/**
 * The water's surface, swell and caustics in one WebGL fragment shader —
 * and, while the WEE section is on screen, the story's layers over them:
 * depth, the aurora wash, the sunrise and the sunlit wash.
 *
 * The app draws these with Skia: gradient rects, six blurred strokes per
 * band, a runtime shader and a radial sunrise. Here all of it is
 * evaluated per pixel in one pass. A blurred stroke has a closed form —
 * a box of the stroke's width convolved with a Gaussian — so the bands
 * need no blur pass: coverage at distance d is
 * ½·[erf((w/2 − d)/σ√2) + erf((w/2 + d)/σ√2)].
 *
 * Compositing matches the app's: a Skia Group's opacity multiplies into
 * each child's paint, so every stroke is its own white layer over what
 * is below, and white over white composes by multiplying what each one
 * leaves showing.
 */

import { colors } from '@config/colors';
import { createLogger } from '@logging';
import {
  BAND_BOB,
  BAND_BODY_BLUR,
  BAND_PRESENCE,
  BAND_RIDGE_BLUR,
  BAND_RIDGE_WIDTH,
  BANDS,
  CAUSTIC_INTENSITY,
  CAUSTIC_OPACITY,
  CAUSTIC_SCALE,
  STAGE,
  SWIRL_COVER,
  SWIRL_LIGHTS,
  SWIRL_ORBIT,
  SWIRL_PERIOD,
  WAKE,
  rgba,
  type StageMetrics,
  type StoryFrame,
  type Wake,
} from './water-field';
import { OMEGA } from '@components/wee/wee-timeline';

/**
 * Most backing pixels the shader fills per frame. The picture is soft
 * except the caustic ridges, so past this the canvas renders below the
 * screen's resolution and the browser scales it up. ~2 MP holds a
 * phone at 2× and a laptop window near 1.3×.
 */
const MAX_PIXELS = 2_000_000;
const MAX_DPR = 2;

const log = createLogger('Water');

const VERTEX = `
attribute vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`;

const FRAGMENT = `
precision highp float;

const float TAU = 6.28318530718;

uniform vec2 uStage;        // stage size, stage units
uniform float uUnit;        // backing pixels per stage unit
uniform float uHeightPx;    // backing height, to put y down like the app
uniform float uT;           // story clock, wraps each cycle
uniform float uOmega;
uniform float uCausticTime; // unwrapped, folded onto its own period

uniform vec3 uSurface[3];   // brand.primaryDark, primary, primaryLight
uniform vec3 uDeep[3];      // brand.extraDark, primaryDark, primary
uniform float uDepth;       // calm: CALM_DEPTH; the story moves it
uniform float uLight;       // 1 - depth / 2: the light softens in deep water

uniform vec4 uBandA[6];     // y (share of H), width, a1, a2
uniform vec4 uBandB[6];     // l1, l2, k1, k2
uniform vec4 uBandGain[6];  // strength * presence
uniform vec4 uBandShape;    // body sigma, ridge half-width, ridge sigma, bob
uniform vec2 uBandAlpha;    // body, ridge (colour tokens' alpha)

uniform vec4 uCaustic;      // scale, intensity, opacity, stage width

// The story's layers (StoryFrame); each is skipped at zero opacity.
uniform vec4 uWashLine;     // x0, y0, x1, y1
uniform float uWash;        // opacity
uniform vec4 uWashCol[5];   // sky, sea, rose, lilac, sky
uniform vec4 uSunrise;      // cx, cy, r, opacity
uniform vec4 uSunriseCol[6];
uniform float uSunlit;      // opacity
uniform vec4 uSunlitCol[2];

// The Result section's swirl (SWIRL_LIGHTS), skipped at zero.
uniform vec4 uWake;         // cursor x, y, reach (stage units), strength
uniform vec3 uWakeWave;     // rings per stage unit, phase, how far the water is pulled

uniform float uSwirl;       // cover: swirl weight * SWIRL_COVER
uniform float uSwirlTime;   // seconds, wrapped at SWIRL_PERIOD
uniform float uSwirlOmega;  // one turn per SWIRL_PERIOD
uniform vec3 uSwirlBase;    // the warm white the lights tint
uniform vec4 uSwirlCol[${SWIRL_LIGHTS.length}];  // rgb + how strongly each tints
uniform vec4 uSwirlHome[${SWIRL_LIGHTS.length}]; // x, y (share of screen), r (share of height), orbit
uniform vec2 uSwirlTurns[${SWIRL_LIGHTS.length}];

// Winitzki's erf, relative error ~1e-4.
float erf1(float x) {
  float x2 = x * x;
  float ax2 = 0.147 * x2;
  float r = sqrt(1.0 - exp(-x2 * (1.2732395 + ax2) / (1.0 + ax2)));
  return x < 0.0 ? -r : r;
}

// A stroke of half-width h, blurred by sigma s, seen at distance d from its centre line.
float stroke(float d, float h, float s) {
  float k = 0.70710678 / s;
  return 0.5 * (erf1((h - d) * k) + erf1((h + d) * k));
}

// The app's caustic fold, unchanged: a point displaced by sines of its
// own displaced position four times; bright where the folds pile up.
float caustic(vec2 xy) {
  vec2 uv = xy / uCaustic.w;
  vec2 p = mod(uv * uCaustic.x, TAU) - 250.0;
  vec2 i = p;
  float c = 1.0;
  const float inten = 0.005;
  for (int n = 0; n < 4; n++) {
    float t = uCausticTime * (1.0 - (3.5 / float(n + 1)));
    i = p + vec2(cos(t - i.x) + sin(t + i.y), sin(t - i.y) + cos(t + i.x));
    c += 1.0 / length(vec2(p.x / (sin(i.x + t) / inten), p.y / (cos(i.y + t) / inten)));
  }
  c /= 4.0;
  c = 1.17 - pow(c, 1.4);
  return clamp(pow(abs(c), 8.0) * uCaustic.y, 0.0, 1.0);
}

vec3 ramp(vec3 a, vec3 b, vec3 c, float g) {
  return g < 0.5 ? mix(a, b, g * 2.0) : mix(b, c, g * 2.0 - 1.0);
}

// Gradients as Skia draws them: colour and alpha interpolated apart,
// clamped past the ends, then laid over what is below.
vec4 washAt(float g) {
  vec4 c = uWashCol[0];
  for (int i = 0; i < 4; i++) {
    c = mix(c, uWashCol[i + 1], clamp((g - float(i) * 0.25) * 4.0, 0.0, 1.0));
  }
  return c;
}

// Stops at 0, .16, .34, .52, .70, 1 — the app's sunrise.
vec4 sunriseAt(float d) {
  vec4 c = uSunriseCol[0];
  c = mix(c, uSunriseCol[1], clamp(d / 0.16, 0.0, 1.0));
  c = mix(c, uSunriseCol[2], clamp((d - 0.16) / 0.18, 0.0, 1.0));
  c = mix(c, uSunriseCol[3], clamp((d - 0.34) / 0.18, 0.0, 1.0));
  c = mix(c, uSunriseCol[4], clamp((d - 0.52) / 0.18, 0.0, 1.0));
  c = mix(c, uSunriseCol[5], clamp((d - 0.70) / 0.30, 0.0, 1.0));
  return c;
}

vec3 over(vec3 below, vec4 c, float opacity) {
  return mix(below, c.rgb, c.a * opacity);
}

// Soft lights on slow orbits over a warm white, the page curling gently
// beneath them so the colour swirls rather than slides.
vec3 swirlAt(vec2 xy) {
  vec2 p = xy / uStage;
  float w = uSwirlTime * uSwirlOmega;
  vec2 q = p + 0.05 * vec2(sin(p.y * 5.0 + 11.0 * w), cos(p.x * 4.0 - 9.0 * w));
  vec2 aspect = vec2(uStage.x / uStage.y, 1.0);
  vec3 c = uSwirlBase;
  for (int i = 0; i < ${SWIRL_LIGHTS.length}; i++) {
    vec4 home = uSwirlHome[i];
    vec2 turns = uSwirlTurns[i];
    float fi = float(i);
    vec2 centre = home.xy + home.w * vec2(sin(turns.x * w + fi * 1.7), cos(turns.y * w + fi * 2.3));
    vec2 d = (q - centre) * aspect;
    float glow = exp(-dot(d, d) / (home.z * home.z));
    c = mix(c, uSwirlCol[i].rgb, uSwirlCol[i].a * glow);
  }
  return c;
}

void main() {
  vec2 xy = vec2(gl_FragCoord.x, uHeightPx - gl_FragCoord.y) / uUnit;

  // The cursor's wake. Nothing is drawn for it: the water's own
  // coordinates are pulled toward and away from the pointer in rings
  // that travel outward, so the swell and the refracted light that were
  // already there are what ripples out of the cursor.
  float bell = 0.0;
  vec2 wxy = xy;
  if (uWake.w > 0.0) {
    vec2 wd = xy - uWake.xy;
    float wr = length(wd);
    bell = uWake.w * exp(-(wr * wr) / (uWake.z * uWake.z));
    float rings = bell * sin(wr * uWakeWave.x - uWakeWave.y);
    wxy = xy + (wd / max(wr, 1.0)) * rings * uWakeWave.z;
  }

  // Surface, and one step down the same ramp at the calm depth — both
  // along the page's own corner-to-corner diagonal, as the app's
  // createLinearGradient((0,0), (W,H)) is.
  float g = clamp(dot(xy, uStage) / dot(uStage, uStage), 0.0, 1.0);
  vec3 col = mix(
    ramp(uSurface[0], uSurface[1], uSurface[2], g),
    ramp(uDeep[0], uDeep[1], uDeep[2], g),
    uDepth
  );

  // Colour, drifting, as the options arrive.
  if (uWash > 0.0) {
    vec2 axis = uWashLine.zw - uWashLine.xy;
    float gw = clamp(dot(xy - uWashLine.xy, axis) / dot(axis, axis), 0.0, 1.0);
    col = over(col, washAt(gw), uWash);
  }

  // The swell.
  float keep = 1.0;
  for (int b = 0; b < 6; b++) {
    vec4 A = uBandA[b];
    vec4 B = uBandB[b];
    float fb = float(b);
    float w1 = TAU / B.x;
    float w2 = TAU / B.y;
    float ph1 = wxy.x * w1 + uT * B.z * uOmega + fb;
    float ph2 = wxy.x * w2 + uT * B.w * uOmega + fb * 1.7;
    float yc = uStage.y * A.x + uBandShape.w * sin(uT * uOmega + fb) + A.z * sin(ph1) + A.w * sin(ph2);
    float slope = A.z * w1 * cos(ph1) + A.w * w2 * cos(ph2);
    float d = abs(wxy.y - yc) * inversesqrt(1.0 + slope * slope);
    if (d < 0.5 * A.y + 4.0 * uBandShape.x) {
      float gain = uBandGain[b].x * uLight;
      keep *= 1.0 - uBandAlpha.x * gain * stroke(d, 0.5 * A.y, uBandShape.x);
      keep *= 1.0 - uBandAlpha.y * gain * stroke(d, uBandShape.y, uBandShape.z);
    }
  }
  col = mix(vec3(1.0), col, keep);

  // Refracted light, rippling out of the cursor with the water it sits
  // on, and gathering a little where the cursor rests.
  col = mix(
    col,
    vec3(1.0),
    uCaustic.z * uLight * (1.0 + ${WAKE.sparkle.toFixed(3)} * bell) * caustic(wxy)
  );

  // The sunrise behind the house as it comes alive, then the white wash
  // that carries the field back to the surface.
  if (uSunrise.w > 0.0) {
    col = over(col, sunriseAt(length(xy - uSunrise.xy) / uSunrise.z), uSunrise.w);
  }
  if (uSunlit > 0.0) {
    col = over(col, mix(uSunlitCol[0], uSunlitCol[1], g), uSunlit);
  }
  if (uSwirl > 0.0) {
    col = mix(col, swirlAt(xy), uSwirl);
  }

  // Half a level of noise, so the long gradient does not band.
  float n = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
  gl_FragColor = vec4(col + (n - 0.5) / 255.0, 1.0);
}
`;

export type WaterGl = {
  /** Size the backing store for a CSS size; call on every resize. */
  resize(cssWidth: number, cssHeight: number): void;
  /**
   * Draw one frame. A no-op until the program has linked, or after the
   * context is lost. `swirlTime` is seconds wrapped at SWIRL_PERIOD;
   * `wake` is the cursor the water is answering, or null for none.
   */
  draw(
    t: number,
    causticTime: number,
    metrics: StageMetrics,
    story: StoryFrame,
    swirlTime: number,
    wake: Wake | null,
  ): void;
  destroy(): void;
};

type Uniforms = Record<string, WebGLUniformLocation | null>;

/**
 * Null when this browser cannot run the water well — no WebGL, no
 * high-precision floats, or only a software renderer. The page keeps
 * its CSS gradient then, which is the same surface without the light.
 *
 * The program may finish compiling frames after this returns, so
 * `onReady` says when `draw` will first work — the caller must paint
 * then, because a still (reduced-motion) page has no loop that would
 * paint again. `onLost` fires when the context is lost, whose canvas
 * shows black until `onReady` fires again on restore.
 */
export function createWaterGl(
  canvas: HTMLCanvasElement,
  onReady: () => void,
  onLost: () => void,
): WaterGl | null {
  const gl = canvas.getContext('webgl', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    powerPreference: 'low-power',
    failIfMajorPerformanceCaveat: true,
  });
  if (!gl) return null;
  const high = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
  // The caustic fold offsets every point by -250; half floats cannot hold it.
  if (!high || high.precision === 0) return null;

  const parallel = gl.getExtension('KHR_parallel_shader_compile');
  let program: WebGLProgram | null = null;
  let buffer: WebGLBuffer | null = null;
  let uniforms: Uniforms = {};
  let linked = false;
  let pollHandle = 0;
  let pxPerCss = 1;

  const compile = (type: number, source: string) => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
  };

  const build = () => {
    linked = false;
    const vs = compile(gl.VERTEX_SHADER, VERTEX);
    const fs = compile(gl.FRAGMENT_SHADER, FRAGMENT);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    program = prog;

    // Compiling a shader this size can take tens of milliseconds on a
    // phone. Where the browser can compile in the background, wait for
    // it frame by frame instead of blocking the page on the first draw.
    const finish = () => {
      if (program !== prog) return;
      if (parallel && !gl.getProgramParameter(prog, parallel.COMPLETION_STATUS_KHR)) {
        pollHandle = requestAnimationFrame(finish);
        return;
      }
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        // A warning, not an error: the page is intact without the light.
        log.warn(
          'shader failed; the background stays a plain gradient',
          gl.getShaderInfoLog(fs) || gl.getProgramInfoLog(prog),
        );
        return;
      }
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      setup(prog);
      linked = true;
      onReady();
    };
    // Always a frame later, so `onReady` never fires before the caller
    // holds the object this function returns.
    pollHandle = requestAnimationFrame(finish);
  };

  const setup = (prog: WebGLProgram) => {
    gl.useProgram(prog);
    buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    // One triangle that covers the viewport.
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(prog, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    uniforms = {};
    for (const name of [
      'uStage',
      'uUnit',
      'uHeightPx',
      'uT',
      'uCausticTime',
      'uDepth',
      'uLight',
      'uWashLine',
      'uWash',
      'uSunrise',
      'uSunlit',
      'uSwirl',
      'uSwirlTime',
      'uWake',
      'uWakeWave',
    ]) {
      uniforms[name] = gl.getUniformLocation(prog, name);
    }
    const at = (name: string) => gl.getUniformLocation(prog, name);
    const rgb = (c: string) => rgba(c).slice(0, 3);
    const { wee } = colors;

    gl.uniform1f(at('uOmega'), OMEGA);
    gl.uniform3fv(
      at('uSurface'),
      [colors.brand.primaryDark, colors.brand.primary, colors.brand.primaryLight].flatMap(rgb),
    );
    gl.uniform3fv(
      at('uDeep'),
      [colors.brand.extraDark, colors.brand.primaryDark, colors.brand.primary].flatMap(rgb),
    );
    gl.uniform4fv(at('uBandA'), BANDS.flatMap((b) => [b.y, b.width, b.a1, b.a2]));
    gl.uniform4fv(at('uBandB'), BANDS.flatMap((b) => [b.l1, b.l2, b.k1, b.k2]));
    gl.uniform4fv(at('uBandGain'), BANDS.flatMap((b) => [b.strength * BAND_PRESENCE, 0, 0, 0]));
    gl.uniform4f(at('uBandShape'), BAND_BODY_BLUR, BAND_RIDGE_WIDTH / 2, BAND_RIDGE_BLUR, BAND_BOB);
    gl.uniform2f(at('uBandAlpha'), rgba(wee.caustic)[3], rgba(wee.causticRidge)[3]);
    gl.uniform4f(at('uCaustic'), CAUSTIC_SCALE, CAUSTIC_INTENSITY, CAUSTIC_OPACITY, STAGE);
    gl.uniform4fv(
      at('uWashCol'),
      [wee.washSky, wee.washSea, wee.washRose, wee.washLilac, wee.washSky].flatMap(rgba),
    );
    gl.uniform4fv(
      at('uSunriseCol'),
      [
        wee.sunriseCore,
        wee.sunriseGold,
        wee.sunrisePeach,
        wee.sunriseRose,
        wee.sunriseLilac,
        wee.sunriseClear,
      ].flatMap(rgba),
    );
    gl.uniform4fv(at('uSunlitCol'), [wee.sunlitFaint, wee.sunlit].flatMap(rgba));

    const swirlColour = (name: (typeof SWIRL_LIGHTS)[number]['color']) =>
      rgb(name === 'seafoam' ? colors.brand.primary : wee[name]);
    gl.uniform1f(at('uSwirlOmega'), (2 * Math.PI) / SWIRL_PERIOD);
    // The blush white at the middle of the mock's Result page.
    gl.uniform3fv(at('uSwirlBase'), rgb(colors.accent.pink.extraLight));
    gl.uniform4fv(at('uSwirlCol'), SWIRL_LIGHTS.flatMap((l) => [...swirlColour(l.color), l.alpha]));
    gl.uniform4fv(at('uSwirlHome'), SWIRL_LIGHTS.flatMap((l) => [l.x, l.y, l.r, SWIRL_ORBIT]));
    gl.uniform2fv(at('uSwirlTurns'), SWIRL_LIGHTS.flatMap((l) => [...l.turns]));
  };

  const lose = (e: Event) => {
    e.preventDefault();
    linked = false;
    program = null;
    onLost();
  };
  const restore = () => build();
  canvas.addEventListener('webglcontextlost', lose);
  canvas.addEventListener('webglcontextrestored', restore);

  build();

  return {
    resize(cssWidth, cssHeight) {
      if (cssWidth <= 0 || cssHeight <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const scale = Math.min(dpr, Math.sqrt(MAX_PIXELS / (cssWidth * cssHeight)));
      const w = Math.max(1, Math.round(cssWidth * scale));
      const h = Math.max(1, Math.round(cssHeight * scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      pxPerCss = w / cssWidth;
      gl.viewport(0, 0, w, h);
    },
    draw(t, causticTime, metrics, story, swirlTime, wake) {
      if (!linked || gl.isContextLost()) return;
      // The wake is authored in CSS px; the stage is those px over `unit`.
      const strength = wake ? wake.strength : 0;
      if (strength > 0 && wake) {
        const unit = metrics.unit;
        const ring = WAKE.ringPx / unit;
        gl.uniform4f(uniforms.uWake, wake.x / unit, wake.y / unit, WAKE.radiusPx / unit, strength);
        gl.uniform3f(
          uniforms.uWakeWave,
          (2 * Math.PI) / ring,
          // Rings travel out at WAKE.speedPx: one ring every ring/speed seconds.
          2 * Math.PI * (WAKE.speedPx / WAKE.ringPx) * wake.time,
          WAKE.pull,
        );
      } else {
        gl.uniform4f(uniforms.uWake, 0, 0, 1, 0);
      }
      gl.uniform2f(uniforms.uStage, metrics.W, metrics.H);
      gl.uniform1f(uniforms.uUnit, metrics.unit * pxPerCss);
      gl.uniform1f(uniforms.uHeightPx, canvas.height);
      gl.uniform1f(uniforms.uT, t);
      gl.uniform1f(uniforms.uCausticTime, causticTime);
      gl.uniform1f(uniforms.uDepth, story.depth);
      gl.uniform1f(uniforms.uLight, story.light);
      const { wash, sunrise } = story;
      gl.uniform4f(uniforms.uWashLine, wash.x0, wash.y0, wash.x1, wash.y1);
      gl.uniform1f(uniforms.uWash, wash.opacity);
      gl.uniform4f(uniforms.uSunrise, sunrise.cx, sunrise.cy, sunrise.r, sunrise.opacity);
      gl.uniform1f(uniforms.uSunlit, story.sunlit);
      gl.uniform1f(uniforms.uSwirl, story.swirl * SWIRL_COVER);
      gl.uniform1f(uniforms.uSwirlTime, swirlTime);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
    destroy() {
      cancelAnimationFrame(pollHandle);
      canvas.removeEventListener('webglcontextlost', lose);
      canvas.removeEventListener('webglcontextrestored', restore);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
      program = null;
      linked = false;
    },
  };
}
